/**
 * consent.js — San Tomas Tourism College
 * ---------------------------------------------------------------
 * Single source of truth for whether the visitor has agreed to
 * marketing/analytics tracking. Both the Meta Pixel and GA4 read it.
 *
 * Kenya's Data Protection Act 2019 treats an advertising pixel as
 * non-essential processing: consent must be obtained before it runs,
 * must be as easy to withdraw as to give, and the choice must be
 * recorded. This module stores the decision with a timestamp and a
 * policy version, so a later change to the privacy notice re-prompts.
 */

const STORAGE_KEY = 'stc_tracking_consent';

/** Bump this when the privacy notice materially changes. */
export const POLICY_VERSION = '2026-07';

/** Consent lapses after 6 months and is re-requested. */
const MAX_AGE_DAYS = 180;

export const CONSENT_EVENT = 'stc:consent-changed';

const read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

/**
 * 'granted' | 'denied' | null (never asked, or the stored answer expired)
 */
export const getConsentState = () => {
  if (typeof window === 'undefined') return null;

  const record = read();
  if (!record || !record.state) return null;
  if (record.version !== POLICY_VERSION) return null;

  const ageDays = (Date.now() - (record.at || 0)) / 86400000;
  if (ageDays > MAX_AGE_DAYS) return null;

  return record.state;
};

export const hasConsent = () => getConsentState() === 'granted';

/** Record a decision and tell the rest of the app about it. */
export const setConsent = (granted) => {
  const state = granted ? 'granted' : 'denied';
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ state, at: Date.now(), version: POLICY_VERSION })
    );
  } catch (e) {
    /* Private browsing with storage blocked — treat as a session-only
       decision. The event below still fires, so the choice is honoured
       for this visit. */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { granted } }));
  return state;
};

/** Clear the decision so the banner appears again — used by "Cookie settings". */
export const resetConsent = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { granted: false } }));
};
